import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Highlight from '@tiptap/extension-highlight';
import { TextStyle } from '@tiptap/extension-text-style';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { Extension } from '@tiptap/core';
import { useEffect, useCallback, useState, useRef } from 'react';

// Custom FontSize extension using TextStyle
const FontSize = Extension.create({
  name: 'fontSize',
  addOptions() {
    return { types: ['textStyle'] };
  },
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (element) => element.style.fontSize || null,
            renderHTML: (attributes) => {
              if (!attributes.fontSize) return {};
              return { style: `font-size: ${attributes.fontSize}` };
            },
          },
        },
      },
    ];
  },
  addCommands() {
    return {
      setFontSize:
        (fontSize: string) =>
        ({ chain }: any) =>
          chain().setMark('textStyle', { fontSize }).run(),
      unsetFontSize:
        () =>
        ({ chain }: any) =>
          chain().setMark('textStyle', { fontSize: null }).removeEmptyTextStyle().run(),
    };
  },
});
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Heading1,
  Heading2,
  Heading3,
  Highlighter,
  Undo,
  Redo,
  RemoveFormatting,
  RefreshCw,
  Pencil,
  MoreVertical,
  ChevronUp,
  ChevronDown,
  Trash2,
} from 'lucide-react';

interface RichTextEditorProps {
  content: string;
  onChange: (html: string) => void;
  placeholder?: string;
  darkMode?: boolean;
  minHeight?: string;
  baseFontSize?: string;
  disabled?: boolean;
  onRefineItem?: (itemHtml: string, instruction: string, mode: 'edit' | 'regenerate') => Promise<string>;
}

const FONT_SIZES = [
  { label: '10', value: '10px' },
  { label: '12', value: '12px' },
  { label: '14', value: '14px' },
  { label: '16', value: '16px' },
  { label: '18', value: '18px' },
  { label: '20', value: '20px' },
  { label: '24', value: '24px' },
  { label: '28', value: '28px' },
];

function MenuBar({ editor, darkMode, currentFontSize }: { editor: ReturnType<typeof useEditor>; darkMode?: boolean; currentFontSize: string }) {
  if (!editor) return null;

  const btnClass = (active: boolean) =>
    `p-1.5 rounded transition-colors ${
      active
        ? darkMode
          ? 'bg-blue-600 text-white'
          : 'bg-blue-100 text-blue-700'
        : darkMode
          ? 'text-slate-300 hover:bg-slate-600 hover:text-white'
          : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
    }`;

  const divider = (
    <div className={`w-px h-5 mx-1 ${darkMode ? 'bg-slate-600' : 'bg-slate-200'}`} />
  );

  const matchedSize = currentFontSize;

  return (
    <div
      className={`flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b ${
        darkMode ? 'border-slate-600 bg-slate-700' : 'border-slate-200 bg-slate-50'
      }`}
    >
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={btnClass(editor.isActive('bold'))}
        title="Fett"
      >
        <Bold className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={btnClass(editor.isActive('italic'))}
        title="Kursiv"
      >
        <Italic className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        className={btnClass(editor.isActive('underline'))}
        title="Unterstrichen"
      >
        <UnderlineIcon className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleHighlight().run()}
        className={btnClass(editor.isActive('highlight'))}
        title="Hervorheben"
      >
        <Highlighter className="w-4 h-4" />
      </button>

      {divider}

      {/* Font Size Dropdown */}
      <select
        translate="no"        value={matchedSize}
        onChange={(e) => {
          const size = e.target.value;
          if (size) {
            (editor.commands as any).setFontSize(size);
          } else {
            (editor.commands as any).unsetFontSize();
          }
          editor.commands.focus();
        }}
        className={`h-7 px-1 rounded text-xs border cursor-pointer ${
          darkMode
            ? 'bg-slate-700 border-slate-600 text-slate-200'
            : 'bg-white border-slate-200 text-slate-700'
        }`}
        title="Schriftgröße"
      >
        <option value="">–</option>
        {FONT_SIZES.map((s) => (
          <option key={s.value} value={s.value}>
            {s.label}
          </option>
        ))}
      </select>

      {divider}

      <button
        type="button"
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        className={btnClass(editor.isActive('heading', { level: 1 }))}
        title="Überschrift 1"
      >
        <Heading1 className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        className={btnClass(editor.isActive('heading', { level: 2 }))}
        title="Überschrift 2"
      >
        <Heading2 className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        className={btnClass(editor.isActive('heading', { level: 3 }))}
        title="Überschrift 3"
      >
        <Heading3 className="w-4 h-4" />
      </button>

      {divider}

      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={btnClass(editor.isActive('bulletList'))}
        title="Aufzählung"
      >
        <List className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={btnClass(editor.isActive('orderedList'))}
        title="Nummerierte Liste"
      >
        <ListOrdered className="w-4 h-4" />
      </button>

      {divider}

      <button
        type="button"
        onClick={() => editor.chain().focus().setTextAlign('left').run()}
        className={btnClass(editor.isActive({ textAlign: 'left' }))}
        title="Linksbündig"
      >
        <AlignLeft className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().setTextAlign('center').run()}
        className={btnClass(editor.isActive({ textAlign: 'center' }))}
        title="Zentriert"
      >
        <AlignCenter className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().setTextAlign('right').run()}
        className={btnClass(editor.isActive({ textAlign: 'right' }))}
        title="Rechtsbündig"
      >
        <AlignRight className="w-4 h-4" />
      </button>

      {divider}

      <button
        type="button"
        onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
        className={btnClass(false)}
        title="Formatierung entfernen"
      >
        <RemoveFormatting className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
        className={`${btnClass(false)} disabled:opacity-30`}
        title="Rückgängig"
      >
        <Undo className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
        className={`${btnClass(false)} disabled:opacity-30`}
        title="Wiederholen"
      >
        <Redo className="w-4 h-4" />
      </button>
    </div>
  );
}

export default function RichTextEditor({
  content,
  onChange,
  placeholder = 'Text eingeben...',
  darkMode = false,
  minHeight = '200px',
  baseFontSize = '14px',
  disabled = false,
  onRefineItem,
}: RichTextEditorProps) {
  // Track current font size at cursor + force re-render on every transaction
  const [currentFontSize, setCurrentFontSize] = useState(baseFontSize);
  const [, forceUpdate] = useState(0);

  // Hover-based context-menu state (works on <li> and <p>)
  const [hoveredLi, setHoveredLi] = useState<HTMLElement | null>(null);
  const [menuLi, setMenuLi] = useState<HTMLElement | null>(null);   // dropdown open
  const [editLi, setEditLi] = useState<HTMLElement | null>(null);   // edit-input open
  const [refineInstruction, setRefineInstruction] = useState('');
  const [refineLoading, setRefineLoading] = useState(false);
  const [refineError, setRefineError] = useState('');
  // 'edit' = Text behalten, nur gezielt ändern (Anweisung Pflicht); 'regenerate' = Absatz neu schreiben.
  const [refineMode, setRefineMode] = useState<'edit' | 'regenerate'>('regenerate');
  const wrapperRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const detectFontSize = useCallback((ed: any) => {
    const { from, to } = ed.state.selection;
    const isRange = from !== to;

    if (isRange) {
      // Bei Textauswahl: prüfe ob alle Zeichen die gleiche Größe haben
      const sizes = new Set<string>();
      ed.state.doc.nodesBetween(from, to, (node: any) => {
        if (node.isText) {
          const mark = node.marks.find((m: any) => m.type.name === 'textStyle');
          sizes.add(mark?.attrs?.fontSize || '');
        }
      });
      if (sizes.size === 1) {
        const onlySize = [...sizes][0];
        setCurrentFontSize(onlySize || baseFontSize);
      } else {
        // Verschiedene Größen in der Auswahl → leer anzeigen
        setCurrentFontSize('');
      }
    } else {
      // Cursor ohne Auswahl
      const fs = ed.getAttributes('textStyle')?.fontSize;
      setCurrentFontSize(fs || baseFontSize);
    }
  }, [baseFontSize]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Highlight,
      TextStyle,
      FontSize,
      Table.configure({ resizable: false, HTMLAttributes: { class: 'tiptap-table' } }),
      TableRow,
      TableCell,
      TableHeader,
    ],
    content: content || '',
    editable: !disabled,
    onUpdate: ({ editor: ed }) => {
      onChange(ed.getHTML());
      detectFontSize(ed);
    },
    onSelectionUpdate: ({ editor: ed }) => {
      detectFontSize(ed);
    },
    onTransaction: ({ editor: ed }) => {
      detectFontSize(ed);
      forceUpdate(n => n + 1);
    },
    editorProps: {
      attributes: {
        class: `focus:outline-none px-3 py-2 leading-relaxed ${
          darkMode ? 'text-white' : 'text-slate-900'
        }`,
        style: `min-height: ${minHeight}; font-size: ${baseFontSize}`,
      },
    },
  });

  // Sync external content changes (e.g. from AI generation)
  const setContent = useCallback(
    (html: string) => {
      if (editor && !editor.isFocused && html !== editor.getHTML()) {
        editor.commands.setContent(html, false);
      }
    },
    [editor]
  );

  useEffect(() => {
    setContent(content);
  }, [content, setContent]);

  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled);
    }
  }, [disabled, editor]);

  // Pencil-Position: oben rechts am aktiven Element
  const getPencilStyle = (el: HTMLElement): React.CSSProperties => {
    if (!wrapperRef.current) return {};
    const rect = el.getBoundingClientRect();
    const wRect = wrapperRef.current.getBoundingClientRect();
    return {
      top: rect.top - wRect.top + 4,
      right: 10,
    };
  };

  // Highlight-Overlay: überlagert das aktive Element (pointer-events:none → blockiert keine Klicks)
  const getHighlightStyle = (el: HTMLElement): React.CSSProperties => {
    if (!wrapperRef.current) return {};
    const rect = el.getBoundingClientRect();
    const wRect = wrapperRef.current.getBoundingClientRect();
    return {
      top: rect.top - wRect.top,
      left: rect.left - wRect.left - 6,
      width: rect.width + 6,
      height: rect.height,
    };
  };

  // Input-Card-Position relativ zum wrapperRef
  const getCardStyle = (el: HTMLElement): React.CSSProperties => {
    if (!wrapperRef.current) return {};
    const rect = el.getBoundingClientRect();
    const wRect = wrapperRef.current.getBoundingClientRect();
    return {
      top: rect.bottom - wRect.top + 4,
      left: rect.left - wRect.left,
      width: Math.min(rect.width, 420),
    };
  };

  // Dropdown-Menü erscheint direkt unter dem Drei-Punkte-Button
  const getMenuStyle = (el: HTMLElement): React.CSSProperties => {
    if (!wrapperRef.current) return {};
    const rect = el.getBoundingClientRect();
    const wRect = wrapperRef.current.getBoundingClientRect();
    return {
      top: rect.top - wRect.top + 32, // unterhalb des Buttons (24px) + 8px Abstand
      right: 10,
    };
  };

  // ProseMirror-Position des Elements finden (LI → listItem, P → paragraph)
  const findNodePos = (el: HTMLElement): { pos: number; size: number; nodeType: string } | null => {
    if (!editor) return null;
    const pmNodeType = el.nodeName === 'LI' ? 'listItem' : 'paragraph';
    try {
      const pmPos = editor.view.posAtDOM(el, 0);
      const $pos = editor.state.doc.resolve(pmPos);
      for (let d = $pos.depth; d >= 0; d--) {
        if ($pos.node(d).type.name === pmNodeType) {
          return { pos: $pos.before(d), size: $pos.node(d).nodeSize, nodeType: pmNodeType };
        }
      }
    } catch { /* ignore */ }
    return null;
  };

  const handleMouseOver = (e: React.MouseEvent) => {
    if (menuLi || editLi) return;
    let el = e.target as Element | null;
    // Walk up DOM to find <li> or <p>
    while (el && el.nodeName !== 'LI' && el.nodeName !== 'P') el = el.parentElement;
    if (el && (el.nodeName === 'LI' || el.nodeName === 'P')) {
      // Skip empty paragraphs
      if (el.nodeName === 'P' && !el.textContent?.trim()) { setHoveredLi(null); return; }
      // Skip structural headings: <p> whose only non-whitespace children are <strong> tags
      if (el.nodeName === 'P') {
        const meaningful = Array.from(el.childNodes).filter(
          n => !(n.nodeType === Node.TEXT_NODE && !n.textContent?.trim())
        );
        if (meaningful.length > 0 && meaningful.every(n => n.nodeName === 'STRONG')) {
          setHoveredLi(null);
          return;
        }
      }
      setHoveredLi(el as HTMLElement);
    } else {
      setHoveredLi(null);
    }
  };

  const handleMouseOut = (e: React.MouseEvent) => {
    if (menuLi || editLi) return;
    const related = e.relatedTarget as Element | null;
    if (!wrapperRef.current?.contains(related)) setHoveredLi(null);
  };

  const closeAll = () => {
    setMenuLi(null);
    setEditLi(null);
    setHoveredLi(null);
    setRefineInstruction('');
    setRefineError('');
  };

  const handleRefine = async () => {
    if (!editor || !onRefineItem || !editLi) return;
    // "Bearbeiten": Anweisung ist Pflicht — ohne sie nichts tun.
    if (refineMode === 'edit' && !refineInstruction.trim()) {
      setRefineError('Bitte beschreibe die gewünschte Änderung.');
      return;
    }
    const el = editLi;
    const itemHtml = el.innerHTML;
    const isListItem = el.nodeName === 'LI';

    const info = findNodePos(el);
    if (!info) return;
    const { pos: nodePos, size: nodeSize } = info;

    setRefineLoading(true);
    setRefineError('');
    try {
      const newHtml = await onRefineItem(itemHtml, refineInstruction, refineMode);
      const insertHtml = isListItem ? `<li>${newHtml}</li>` : `<p>${newHtml}</p>`;
      editor.chain()
        .focus()
        .deleteRange({ from: nodePos, to: nodePos + nodeSize })
        .insertContentAt(nodePos, insertHtml)
        .run();
      closeAll();
    } catch {
      setRefineError('Fehler beim Verfeinern. Bitte erneut versuchen.');
    } finally {
      setRefineLoading(false);
    }
  };

  const handleDeleteItem = () => {
    if (!editor || !menuLi) return;
    const info = findNodePos(menuLi);
    if (!info) return;
    editor.chain().focus().deleteRange({ from: info.pos, to: info.pos + info.size }).run();
    closeAll();
  };

  const handleMoveItem = (direction: 'up' | 'down') => {
    if (!editor || !menuLi) return;
    const el = menuLi;
    try {
      const pmPos = editor.view.posAtDOM(el, 0);
      const $pos = editor.state.doc.resolve(pmPos);
      const pmNodeType = el.nodeName === 'LI' ? 'listItem' : 'paragraph';

      let nodeDepth = -1;
      for (let d = $pos.depth; d >= 0; d--) {
        if ($pos.node(d).type.name === pmNodeType) { nodeDepth = d; break; }
      }
      if (nodeDepth === -1) return;

      const nodeStart = $pos.before(nodeDepth);
      const node = $pos.node(nodeDepth);
      const nodeEnd = nodeStart + node.nodeSize;
      const parent = $pos.node(nodeDepth - 1);
      const indexInParent = $pos.index(nodeDepth - 1);

      const { tr } = editor.state;

      if (direction === 'up') {
        if (indexInParent === 0) return;
        const prevNode = parent.child(indexInParent - 1);
        const prevStart = nodeStart - prevNode.nodeSize;
        const nodeSlice = editor.state.doc.slice(nodeStart, nodeEnd);
        const prevSlice = editor.state.doc.slice(prevStart, nodeStart);
        tr.delete(prevStart, nodeEnd);
        tr.insert(prevStart, nodeSlice.content);
        tr.insert(prevStart + node.nodeSize, prevSlice.content);
      } else {
        if (indexInParent >= parent.childCount - 1) return;
        const nextNode = parent.child(indexInParent + 1);
        const nextEnd = nodeEnd + nextNode.nodeSize;
        const nodeSlice = editor.state.doc.slice(nodeStart, nodeEnd);
        const nextSlice = editor.state.doc.slice(nodeEnd, nextEnd);
        tr.delete(nodeStart, nextEnd);
        tr.insert(nodeStart, nextSlice.content);
        tr.insert(nodeStart + nextNode.nodeSize, nodeSlice.content);
      }

      editor.view.dispatch(tr);
    } catch { /* ignore */ }
    closeAll();
  };

  // Escape schließt Menü und Edit-Card
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeAll(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Klick außerhalb schließt Menü und Edit-Card
  useEffect(() => {
    if (!menuLi && !editLi) return;
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      const inMenu = menuRef.current?.contains(target);
      const inCard = cardRef.current?.contains(target);
      const inButton = buttonRef.current?.contains(target);
      if (!inMenu && !inCard && !inButton) closeAll();
    };
    const timer = setTimeout(() => document.addEventListener('mousedown', onClick), 0);
    return () => { clearTimeout(timer); document.removeEventListener('mousedown', onClick); };
  }, [menuLi, editLi]);


  const activeLi = editLi || menuLi || hoveredLi;

  return (
    // Outer wrapper: position:relative für absolut positionierte Kinder (Stift + Input-Card)
    <div ref={wrapperRef} className={`relative ${disabled ? 'opacity-60' : ''}`}>
      {/* Editor-Box */}
      <div className={`rounded-lg border overflow-hidden ${darkMode ? 'border-slate-600 bg-slate-800' : 'border-slate-300 bg-white'}`}>
        <MenuBar editor={editor} darkMode={darkMode} currentFontSize={currentFontSize} />
        <div
          className={`${darkMode ? 'bg-slate-800' : 'bg-white'}`}
          style={{ minHeight }}
          onMouseOver={onRefineItem ? handleMouseOver : undefined}
          onMouseOut={onRefineItem ? handleMouseOut : undefined}
        >
          {editor?.isEmpty && !editor.isFocused && (
            <div className={`absolute px-3 py-2 text-sm pointer-events-none ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
              {placeholder}
            </div>
          )}
          <EditorContent editor={editor} />
          <style>{`
            .ProseMirror { font-size: 14px !important; }
            .ProseMirror * { font-size: inherit; }
            .tiptap-table { border-collapse: collapse; width: 100%; margin: 8px 0; font-size: 14px; }
            .tiptap-table td, .tiptap-table th { border: none; padding: 6px 10px; text-align: left; }
            .tiptap-table th { font-weight: 600; }
            .dark .tiptap-table th { background-color: #1e293b; }
            .ProseMirror hr { border: none; border-top: 2px solid #1e293b; margin: 0; }
            .dark .ProseMirror hr { border-top-color: #94a3b8; }
            .ProseMirror h1 { font-size: 1.6em; font-weight: 700; margin: 0.8em 0 0.4em; line-height: 1.2; }
            .ProseMirror h2 { font-size: 1.3em; font-weight: 700; margin: 0.7em 0 0.35em; line-height: 1.2; }
            .ProseMirror h3 { font-size: 1.1em; font-weight: 600; margin: 0.6em 0 0.3em; line-height: 1.2; }
            .ProseMirror ul { list-style-type: disc; padding-left: 1.5em; margin: 0.5em 0; }
            .ProseMirror ol { list-style-type: decimal; padding-left: 1.5em; margin: 0.5em 0; }
            .ProseMirror li { margin: 0.25em 0; }
            .ProseMirror table { border-collapse: collapse; width: 100%; margin: 8px 0; }
            .ProseMirror table td, .ProseMirror table th { border: 1px solid #cbd5e1; padding: 6px 10px; text-align: left; }
            .ProseMirror table th { font-weight: 600; background-color: #f1f5f9; }
            .dark .ProseMirror table td, .dark .ProseMirror table th { border-color: #475569; }
            .dark .ProseMirror table th { background-color: #1e293b; }
          `}</style>
        </div>
      </div>

      {/* Highlight-Overlay: React-div über dem aktiven Element, blockiert keine Events */}
      {onRefineItem && activeLi && (
        <div
          style={{ ...getHighlightStyle(activeLi), position: 'absolute', pointerEvents: 'none', zIndex: 1 }}
          className="rounded-r border-l-2 border-blue-400/60 bg-blue-500/10"
        />
      )}

      {/* Drei-Punkte-Button: erscheint beim Hover */}
      {onRefineItem && activeLi && (
        <button
          ref={buttonRef}
          type="button"
          onMouseEnter={() => setHoveredLi(activeLi)}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            if (menuLi === activeLi) { closeAll(); }
            else { setEditLi(null); setMenuLi(activeLi); setRefineInstruction(''); setRefineError(''); }
          }}
          style={{ ...getPencilStyle(activeLi), position: 'absolute' }}
          className={`z-10 flex items-center justify-center w-6 h-6 rounded-full shadow transition-colors ${
            menuLi === activeLi || editLi === activeLi
              ? 'bg-blue-600 text-white'
              : darkMode
                ? 'bg-slate-600 text-slate-300 hover:bg-blue-600 hover:text-white'
                : 'bg-white border border-slate-300 text-slate-400 hover:bg-blue-600 hover:text-white hover:border-blue-600'
          }`}
          title="Optionen"
        >
          <MoreVertical className="w-3 h-3" />
        </button>
      )}

      {/* Dropdown-Menü */}
      {onRefineItem && menuLi && !editLi && (
        <div
          ref={menuRef}
          style={{ ...getMenuStyle(menuLi), position: 'absolute' }}
          className={`z-20 min-w-[130px] rounded-lg shadow-lg border overflow-hidden text-xs font-normal ${
            darkMode ? 'bg-slate-700 border-slate-500' : 'bg-white border-slate-200'
          }`}
        >
          {/* Bearbeiten — Text behalten, gezielt per Anweisung ändern/ergänzen (KI) */}
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => { setRefineMode('edit'); setRefineInstruction(''); setRefineError(''); setEditLi(menuLi); setMenuLi(null); }}
            className={`w-full flex items-center gap-2 px-3 py-1.5 text-left transition-colors ${
              darkMode ? 'text-slate-200 hover:bg-slate-600' : 'text-slate-700 hover:bg-slate-50'
            }`}
          >
            <Pencil className="w-3.5 h-3.5" />
            Bearbeiten
          </button>

          {/* Neu generieren — Absatz frisch neu schreiben (KI) */}
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => { setRefineMode('regenerate'); setRefineInstruction(''); setRefineError(''); setEditLi(menuLi); setMenuLi(null); }}
            className={`w-full flex items-center gap-2 px-3 py-1.5 text-left transition-colors ${
              darkMode ? 'text-slate-200 hover:bg-slate-600' : 'text-slate-700 hover:bg-slate-50'
            }`}
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Neu generieren
          </button>

          {/* Trennstrich */}
          <div className={`border-t ${darkMode ? 'border-slate-600' : 'border-slate-200'}`} />

          {/* Nach oben */}
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => handleMoveItem('up')}
            className={`w-full flex items-center gap-2 px-3 py-1.5 text-left transition-colors ${
              darkMode ? 'text-slate-200 hover:bg-slate-600' : 'text-slate-700 hover:bg-slate-50'
            }`}
          >
            <ChevronUp className="w-3.5 h-3.5" />
            Nach oben
          </button>

          {/* Löschen */}
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleDeleteItem}
            className={`w-full flex items-center gap-2 px-3 py-1.5 text-left transition-colors ${
              darkMode ? 'text-red-400 hover:bg-slate-600' : 'text-red-600 hover:bg-red-50'
            }`}
          >
            <Trash2 className="w-3.5 h-3.5" />
            Löschen
          </button>

          {/* Nach unten */}
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => handleMoveItem('down')}
            className={`w-full flex items-center gap-2 px-3 py-1.5 text-left transition-colors ${
              darkMode ? 'text-slate-200 hover:bg-slate-600' : 'text-slate-700 hover:bg-slate-50'
            }`}
          >
            <ChevronDown className="w-3.5 h-3.5" />
            Nach unten
          </button>
        </div>
      )}

      {/* Edit-Input-Card: erscheint nach Klick auf "Bearbeiten" */}
      {onRefineItem && editLi && (
        <div
          ref={cardRef}
          style={{ ...getCardStyle(editLi), position: 'absolute' }}
          className={`z-20 flex flex-col gap-1.5 p-2 rounded-lg shadow-lg border text-xs ${
            darkMode ? 'bg-slate-700 border-slate-500' : 'bg-white border-slate-200'
          }`}
        >
          <div className="flex gap-1.5 items-center">
            <input
              translate="no"              autoFocus
              type="text"
              value={refineInstruction}
              onChange={(e) => setRefineInstruction(e.target.value.slice(0, refineMode === 'edit' ? 120 : 60))}
              placeholder={refineMode === 'edit' ? 'Was ändern oder ergänzen? (Pflicht)' : 'Anweisung (optional, max. 60 Zeichen)'}
              maxLength={refineMode === 'edit' ? 120 : 60}
              className={`flex-1 px-2 py-1 rounded border text-xs outline-none ${
                darkMode
                  ? 'bg-slate-600 border-slate-500 text-slate-100 placeholder-slate-400'
                  : 'bg-white border-slate-300 text-slate-800 placeholder-slate-400'
              }`}
              onKeyDown={(e) => { if (e.key === 'Enter') handleRefine(); }}
            />
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={handleRefine}
              disabled={refineLoading || (refineMode === 'edit' && !refineInstruction.trim())}
              className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium whitespace-nowrap transition-colors text-white ${
                refineLoading || (refineMode === 'edit' && !refineInstruction.trim())
                  ? 'bg-blue-600 opacity-50 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              <RefreshCw className={`w-3 h-3 ${refineLoading ? 'animate-spin' : ''}`} />
              {refineLoading ? 'Lädt…' : refineMode === 'edit' ? 'Übernehmen' : 'Neu generieren'}
            </button>
          </div>
          {refineError && <p className="text-red-400">{refineError}</p>}
        </div>
      )}
    </div>
  );
}
